import { useCallback, useContext, useEffect, useMemo } from 'react'

import AppContext from '@/context/AppContext'
import routes from '@/Router/routes.constants'

import { summaryInputsResetPaths } from './constants'
import { areAmountMapsEqual, computeSummary, getContextWithResetSummaryInputs, getFeeMessages } from './helpers'

const useSummary = () => {
  const { context, setContext } = useContext(AppContext)
  const isProductSelectionPage = window.location.pathname === routes.productSelectionPage

  const items = context?.items
  const deliveryMethod = context?.orderInfo?.deliveryMethod
  const carbonOffset = context?.orderInfo?.carbonOffset
  const type = context?.orderInfo?.type
  const currency = context?.orderInfo?.currency
  const orderAmount = context?.orderAmount
  const summaryInputs = context?.summaryInputs

  const summary = useMemo(() => {
    return computeSummary({
      items,
      orderInfo: { deliveryMethod, carbonOffset, type, currency },
      orderAmount,
      summaryInputs,
      isProductSelectionPage,
    })
  }, [items, deliveryMethod, carbonOffset, type, currency, orderAmount, summaryInputs, isProductSelectionPage])

  const setFees = useCallback(
    ({ fees }) => {
      setContext(context => {
        const currentFees = context?.summaryInputs?.fees || {}
        const nextFees = { ...currentFees, ...fees }

        if (areAmountMapsEqual(currentFees, nextFees)) {
          return context
        }

        return {
          ...context,
          summaryInputs: {
            ...context?.summaryInputs,
            fees: nextFees,
          },
        }
      })
    },
    [setContext]
  )

  const setBonuses = useCallback(
    bonuses => {
      setContext(context => {
        const currentBonuses = context?.summaryInputs?.bonuses || {}
        const nextBonuses = { ...currentBonuses, ...bonuses }

        if (areAmountMapsEqual(currentBonuses, nextBonuses)) {
          return context
        }

        return {
          ...context,
          summaryInputs: {
            ...context?.summaryInputs,
            bonuses: nextBonuses,
          },
        }
      })
    },
    [setContext]
  )

  const setPreviewFees = useCallback(
    previewFees => {
      setContext(context => {
        const currentPreviewFees = context?.summaryInputs?.previewFees || {}

        if (areAmountMapsEqual(currentPreviewFees, previewFees)) {
          return context
        }

        return {
          ...context,
          summaryInputs: {
            ...context?.summaryInputs,
            previewFees,
          },
        }
      })
    },
    [setContext]
  )

  const setPreviewBonuses = useCallback(
    previewBonuses => {
      setContext(context => {
        const currentPreviewBonuses = context?.summaryInputs?.previewBonuses || {}

        if (areAmountMapsEqual(currentPreviewBonuses, previewBonuses)) {
          return context
        }

        return {
          ...context,
          summaryInputs: {
            ...context?.summaryInputs,
            previewBonuses,
          },
        }
      })
    },
    [setContext]
  )

  const setPreviewFeeMessages = useCallback(
    previewFeeMessages => {
      setContext(context => ({
        ...context,
        summaryInputs: {
          ...context?.summaryInputs,
          previewFeeMessages,
        },
      }))
    },
    [setContext]
  )

  useEffect(() => {
    if (summaryInputsResetPaths.includes(window.location.pathname)) {
      setContext(context => getContextWithResetSummaryInputs(context, { resetDeliveryMethod: isProductSelectionPage }))
    }
  }, [setContext, isProductSelectionPage])

  return {
    summary,
    setFees,
    setPreviewFees,
    setPreviewBonuses,
    setPreviewFeeMessages,
    setBonuses,
    summaryCollapsed: context?.summaryCollapsed,
    storeCurrency: context?.merchantInfo?.currency,
    feeMessages: getFeeMessages({
      isProductSelectionPage,
      persistedFeeMessages: context?.orderInfo?.fee_messages,
      previewFeeMessages: summaryInputs?.previewFeeMessages,
    }),
  }
}

export default useSummary
